/**
 * Platform Settings - Super-Admin only
 * Configure system-wide settings like health check URLs
 */
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation } from "convex/react";
import { api } from "@jourfx/convex/client";
import {
  Settings as SettingsIcon,
  Server,
  Globe,
  Video,
  Database,
  Mail,
  Save,
  Loader2,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  ExternalLink,
  Shield,
  FlaskConical,
  MessageSquareText,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Input,
  Label,
  Skeleton,
  Switch,
  useToast,
} from "@jourfx/ui";
import { SuperAdminGuard } from "../components/SuperAdminGuard";
import { PageHeader } from "../components/PageHeader";
import { Link } from "@tanstack/react-router";

interface Setting {
  key: string;
  label: string;
  description: string;
  category: string;
  placeholder: string;
  value: string;
  updatedAt?: number;
}

// Category icons and colors
const categoryConfig: Record<string, { icon: typeof Server; color: string; bgColor: string }> = {
  healthCheck: {
    icon: Server,
    color: "text-success",
    bgColor: "bg-success-muted",
  },
};

// Setting icons based on key
const settingIcons: Record<string, typeof Globe> = {
  "healthCheck.internalAppUrl": Globe,
  "healthCheck.clientAppUrl": Globe,
  "healthCheck.livekitUrl": Video,
  "healthCheck.minioUrl": Database,
  "healthCheck.smtpHost": Mail,
  "healthCheck.smtpPort": Mail,
};

function getCategoryTitle(
  t: (key: string) => string,
  category: string
): string {
  if (category === "healthCheck") {
    return t("categories.healthCheck.title");
  }
  return "Health Check URLs";
}

function getCategoryDescription(
  t: (key: string) => string,
  category: string
): string {
  if (category === "healthCheck") {
    return t("categories.healthCheck.description");
  }
  return "URLs für die Systemüberwachung konfigurieren";
}

function SettingsContent() {
  const { t } = useTranslation("settings");
  const { t: tc } = useTranslation("common");
  const { toast } = useToast();

  const settings = useQuery(api.admin.systemSettings.getAll);
  const setMultiple = useMutation(api.admin.systemSettings.setMultiple);
  const setSetting = useMutation(api.admin.systemSettings.set);
  const testingMode = useQuery(api.admin.systemSettings.getTestingMode);
  const feedbackEnabled = useQuery(api.admin.systemSettings.getFeedbackEnabled);

  const [localSettings, setLocalSettings] = useState<Record<string, string>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Initialize local state when settings load
  useEffect(() => {
    if (settings) {
      const initial: Record<string, string> = {};
      settings.forEach((s: Setting) => {
        initial[s.key] = s.value;
      });
      setLocalSettings(initial);
    }
  }, [settings]);

  const handleChange = (key: string, value: string) => {
    setLocalSettings((prev) => ({ ...prev, [key]: value }));
    setHasChanges(true);
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    if (!settings) return;

    setIsSaving(true);
    try {
      const changedSettings = settings
        .filter((s: Setting) => localSettings[s.key] !== s.value)
        .map((s: Setting) => ({ key: s.key, value: localSettings[s.key] || "" }));

      if (changedSettings.length > 0) {
        await setMultiple({ settings: changedSettings });
      }

      setHasChanges(false);
      setSaveSuccess(true);
      toast({
        title: t("saveSuccess", "Einstellungen gespeichert"),
        description: t("saveSuccessMessage", "Ihre Änderungen wurden erfolgreich gespeichert."),
      });

      // Reset success indicator after 3 seconds
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      toast({
        title: tc("error.title"),
        description: error instanceof Error ? error.message : tc("error.unknown"),
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    if (settings) {
      const initial: Record<string, string> = {};
      settings.forEach((s: Setting) => {
        initial[s.key] = s.value;
      });
      setLocalSettings(initial);
      setHasChanges(false);
      setSaveSuccess(false);
    }
  };

  // Loading state
  if (settings === undefined) {
    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
        <div className="space-y-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <Skeleton className="h-5 w-32" />
                  <Skeleton className="h-10 w-full" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  // Group settings by category
  const groupedSettings = settings.reduce((acc: Record<string, Setting[]>, setting: Setting) => {
    // system category settings have dedicated UI (e.g. Testing Mode toggle)
    if (setting.category === "system") return acc;
    if (!acc[setting.category]) {
      acc[setting.category] = [];
    }
    acc[setting.category].push(setting);
    return acc;
  }, {});

  const formatTimeAgo = (timestamp: number) => {
    const seconds = Math.floor((Date.now() - timestamp) / 1000);
    if (seconds < 60) return t("timeAgo.seconds", { count: seconds });
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return t("timeAgo.minutes", { count: minutes });
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return t("timeAgo.hours", { count: hours });
    const days = Math.floor(hours / 24);
    return t("timeAgo.days", { count: days });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title={t("title", "Plattform-Einstellungen")}
        description={t("subtitle", "Globale Konfiguration für Ihre JourFx-Installation")}
        actions={
          <div className="flex items-center gap-3">
            {hasChanges && (
              <Button variant="outline" onClick={handleReset} disabled={isSaving}>
                <RefreshCw className="mr-2 h-4 w-4" />
                {tc("actions.reset", "Zurücksetzen")}
              </Button>
            )}
            <Button
              onClick={handleSave}
              disabled={!hasChanges || isSaving}
              className={saveSuccess ? "bg-success text-success-foreground hover:bg-success/90" : ""}
            >
              {isSaving ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : saveSuccess ? (
                <CheckCircle className="mr-2 h-4 w-4" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              {saveSuccess ? t("saved", "Gespeichert") : tc("actions.save", "Speichern")}
            </Button>
          </div>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>{tc("branding", "Branding")}</CardTitle>
          <CardDescription>
            {t("branding.description", "Runtime branding for the Internal + Client apps.")}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex items-center justify-between gap-4">
          <div className="text-sm text-muted-foreground">
            {t("branding.hint", "Logos, colors, typography, and curated text overrides.")}
          </div>
          <Button asChild>
            <Link to="/settings/branding">
              {t("branding.open", "Open branding")}
            </Link>
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Video className="h-5 w-5" />
            Video-Einstellungen
          </CardTitle>
          <CardDescription>
            Hintergrundbilder und Einstellungen für Videosprechstunden.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex items-center justify-between gap-4">
          <div className="text-sm text-muted-foreground">
            Virtuelle Hintergrundbilder für alle Nutzer verwalten.
          </div>
          <Button asChild>
            <Link to="/settings/video">
              Video-Einstellungen öffnen
            </Link>
          </Button>
        </CardContent>
      </Card>

      {/* Testing Mode */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FlaskConical className="h-5 w-5" />
            Testmodus
          </CardTitle>
          <CardDescription>
            Zeitbeschränkungen (Vorbereitungsfenster) in allen Apps deaktivieren, um Termine jederzeit testen zu können.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex items-center justify-between gap-4">
          <div className="text-sm text-muted-foreground">
            {testingMode?.enabled
              ? "Testmodus ist aktiv – Zeitbeschränkungen sind deaktiviert."
              : "Testmodus ist deaktiviert – normale Zeitbeschränkungen gelten."}
          </div>
          <Switch
            checked={testingMode?.enabled ?? false}
            onCheckedChange={async (checked) => {
              try {
                await setSetting({ key: "system.testingMode", value: String(checked) });
                toast({
                  title: checked ? "Testmodus aktiviert" : "Testmodus deaktiviert",
                  description: checked
                    ? "Zeitbeschränkungen sind jetzt deaktiviert."
                    : "Normale Zeitbeschränkungen gelten wieder.",
                });
              } catch (error) {
                toast({
                  title: tc("error.title"),
                  description: error instanceof Error ? error.message : tc("error.unknown"),
                  variant: "destructive",
                });
              }
            }}
          />
        </CardContent>
      </Card>

      {/* Session Feedback Toggle */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquareText className="h-5 w-5" />
            {t("feedback.title", "Sitzungs-Feedback")}
          </CardTitle>
          <CardDescription>
            {t("feedback.description", "Feedback-Formular für Versicherte nach Videosprechstunden anzeigen.")}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex items-center justify-between gap-4">
          <div className="text-sm text-muted-foreground">
            {feedbackEnabled?.enabled
              ? t("feedback.enabledHint", "Versicherte können nach der Sitzung eine Bewertung abgeben.")
              : t("feedback.disabledHint", "Versicherte sehen nach der Sitzung nur eine Abschlussseite ohne Feedback-Formular.")}
          </div>
          <Switch
            checked={feedbackEnabled?.enabled ?? true}
            onCheckedChange={async (checked) => {
              try {
                await setSetting({ key: "system.feedbackEnabled", value: String(checked) });
                toast({
                  title: checked
                    ? t("feedback.enabled", "Feedback aktiviert")
                    : t("feedback.disabled", "Feedback deaktiviert"),
                  description: checked
                    ? t("feedback.enabledMessage", "Versicherte können jetzt Feedback abgeben.")
                    : t("feedback.disabledMessage", "Versicherte sehen nur noch die Abschlussseite."),
                });
              } catch (error) {
                toast({
                  title: tc("error.title"),
                  description: error instanceof Error ? error.message : tc("error.unknown"),
                  variant: "destructive",
                });
              }
            }}
          />
        </CardContent>
      </Card>

      {/* Microsoft Entra ID — planned feature, disabled for now */}
      <Card className="opacity-60">
        <CardHeader className="border-b bg-muted/30">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
              <Shield className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-lg flex items-center gap-2">
                {t("entraId.title", "Microsoft Entra ID")}
                <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium text-muted-foreground">
                  {t("entraId.comingSoon", "Geplant")}
                </span>
              </CardTitle>
              <CardDescription>
                {t("entraId.description", "Single Sign-On (SSO) für die Mitarbeiter-Anmeldung über Microsoft Entra ID konfigurieren.")}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          <div className="space-y-2">
            <Label className="text-sm font-medium text-muted-foreground">
              {t("entraId.tenantId", "Tenant-ID")}
            </Label>
            <Input disabled placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" className="font-mono text-sm" />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium text-muted-foreground">
              {t("entraId.clientId", "Client-ID (Application ID)")}
            </Label>
            <Input disabled placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" className="font-mono text-sm" />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-medium text-muted-foreground">
              {t("entraId.clientSecret", "Client Secret")}
            </Label>
            <Input disabled type="password" placeholder="••••••••••••••••" className="font-mono text-sm" />
          </div>
          <p className="text-xs text-muted-foreground">
            {t("entraId.hint", "Diese Funktion wird in einem zukünftigen Update verfügbar sein. Mitarbeiter können sich dann über ihr Microsoft-Konto anmelden.")}
          </p>
        </CardContent>
      </Card>

      {/* Info banner */}
      <div className="flex items-start gap-3 rounded-lg border border-info-border bg-info-muted p-4">
        <Shield className="h-5 w-5 text-info mt-0.5" />
        <div>
          <p className="font-medium text-info-muted-foreground">
            {t("superAdminOnly", "Nur für Super-Administratoren")}
          </p>
          <p className="text-sm text-info-muted-foreground/80">
            {t("superAdminOnlyMessage", "Diese Einstellungen betreffen die gesamte Plattform. Änderungen werden sofort wirksam.")}
          </p>
        </div>
      </div>

      {/* Settings groups */}
      {Object.entries(groupedSettings).map(([category, categorySettings]) => {
        const config = categoryConfig[category] || categoryConfig.healthCheck;
        const CategoryIcon = config.icon;

        return (
          <Card key={category} className="overflow-hidden">
            <CardHeader className="border-b bg-muted/30">
              <div className="flex items-center gap-3">
                <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${config.bgColor}`}>
                  <CategoryIcon className={`h-5 w-5 ${config.color}`} />
                </div>
                <div>
                  <CardTitle className="text-lg">
                    {getCategoryTitle(t, category)}
                  </CardTitle>
                  <CardDescription>
                    {getCategoryDescription(t, category)}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-border">
                {(categorySettings as Setting[]).map((setting) => {
                  const SettingIcon = settingIcons[setting.key] || SettingsIcon;
                  const hasChanged = localSettings[setting.key] !== setting.value;

                  return (
                    <div
                      key={setting.key}
                      className={`p-6 transition-colors ${hasChanged ? "bg-warning-muted/50" : ""}`}
                    >
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                        <div className="flex items-start gap-3 flex-1">
                          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted">
                            <SettingIcon className="h-4 w-4 text-muted-foreground" />
                          </div>
                          <div className="flex-1 space-y-1">
                            <Label
                              htmlFor={setting.key}
                              className="text-sm font-medium flex items-center gap-2"
                            >
                              {setting.label}
                              {hasChanged && (
                                <span className="text-xs text-warning">
                                  ({t("modified", "geändert")})
                                </span>
                              )}
                            </Label>
                            <p className="text-xs text-muted-foreground">
                              {setting.description}
                            </p>
                            {setting.updatedAt && (
                              <p className="text-xs text-muted-foreground/70">
                                {t("lastUpdated", "Zuletzt aktualisiert")}: {formatTimeAgo(setting.updatedAt)}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="sm:w-80">
                          <div className="relative">
                            <Input
                              id={setting.key}
                              type="text"
                              value={localSettings[setting.key] || ""}
                              onChange={(e) => handleChange(setting.key, e.target.value)}
                              placeholder={setting.placeholder}
                              className={`pr-10 font-mono text-sm ${hasChanged ? "border-warning-border" : ""}`}
                            />
                            {localSettings[setting.key] && (
                              <a
                                href={localSettings[setting.key]}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                                title={t("openInNewTab", "In neuem Tab öffnen")}
                              >
                                <ExternalLink className="h-4 w-4" />
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* Empty state if no settings */}
      {Object.keys(groupedSettings).length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-muted blur-xl" />
              <SettingsIcon className="relative h-12 w-12 text-muted-foreground" />
            </div>
            <h3 className="mt-4 text-lg font-medium text-foreground">
              {t("noSettings", "Keine Einstellungen verfügbar")}
            </h3>
            <p className="mt-1 text-sm text-muted-foreground text-center max-w-sm">
              {t("noSettingsMessage", "Es sind derzeit keine konfigurierbaren Einstellungen vorhanden.")}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Floating save bar when changes exist */}
      {hasChanges && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50">
          <div className="flex items-center gap-4 rounded-full border bg-card px-6 py-3 shadow-lg">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-warning" />
              <span className="text-sm font-medium">
                {t("unsavedChanges", "Nicht gespeicherte Änderungen")}
              </span>
            </div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={handleReset} disabled={isSaving}>
                {tc("actions.discard", "Verwerfen")}
              </Button>
              <Button size="sm" onClick={handleSave} disabled={isSaving}>
                {isSaving && <Loader2 className="mr-2 h-3 w-3 animate-spin" />}
                {tc("actions.save", "Speichern")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function Settings() {
  return (
    <SuperAdminGuard>
      <SettingsContent />
    </SuperAdminGuard>
  );
}
